// src/config/operationalSources.js
//
// Pass 81 — the minimal, EXPLICIT, season-scoped source-config seam (§29). ONE organization (`sas-niseko`) can
// consume TWO genuinely distinct operational business/source relationships without merging their truth:
//   1. SAS SCHOOL   — the "Schedule Overveiw" grid tab of the master workbook (LIVE).
//   2. SAS COLLECTIVE — a SEPARATE workbook (currently NOT connected — no spreadsheet id available).
//
// This registry keeps source-specific ids/tabs/prefixes OUT of the core operational-commitment planner: the planner
// takes a source descriptor, this module owns the SAS-specific descriptors. It is NOT the Pass-83 broad
// organization/location/service configuration system — it is the smallest declarative seam a sync lane needs.
//
// A descriptor NEVER carries identity. Guest text and instructor labels are source EVIDENCE resolved by the planner
// against canonical members; this config only says WHERE a source is and WHAT authority it may hold.

import { SAS_MASTER_SPREADSHEET_ID } from '../utils/sheetSync/bookingDataMaster';
import { isCurrentSasTeachingMember, isCurrentTeachingMember } from '../utils/instructorCategory';

// Authority a source may legitimately hold (§4). A schedule grid is operational-allocation evidence, NOT commercial
// booking truth, unless the source independently provides a stable commercial booking identity (§18/§19).
export const SOURCE_AUTHORITY = {
  OPERATIONAL_ALLOCATION: 'operational_allocation', // blocks availability; never client/revenue/lesson
  COMMERCIAL_BOOKING: 'commercial_booking',         // sold/scheduled booking truth (requires stable booking id)
  REVIEW_ONLY: 'review_only',                       // insufficient to be authoritative — operator review only
};

export const SOURCE_KIND = {
  OPERATIONAL_SCHEDULE_GRID: 'operational_schedule_grid',
};

// ── SAS SCHOOL operational schedule grid (LIVE). Byte-for-byte the current behavior: same sourceId, same tab, same
// SASHOLD hold-id prefix, same authoritative-supply predicate. ────────────────────────────────────────────────────
export const SAS_SCHOOL_SCHEDULE_SOURCE = Object.freeze({
  organizationId: 'sas-niseko',
  seasonId: '2026_27',
  sourceId: 'sas_schedule_grid',
  kind: SOURCE_KIND.OPERATIONAL_SCHEDULE_GRID,
  authorityType: SOURCE_AUTHORITY.OPERATIONAL_ALLOCATION,
  holdIdPrefix: 'SASHOLD',            // MUST stay 'SASHOLD' — existing hold ids are byte-for-byte stable
  spreadsheetId: SAS_MASTER_SPREADSHEET_ID,
  tab: 'Schedule Overveiw',           // real (mis)spelled tab name in the master workbook
  label: 'SAS School schedule',
  // Operational eligibility (teaching + current). SAS School keeps its accepted category-based predicate + its alias
  // config; it does NOT require an explicit per-member source-relationship confirmation (back-compat, §10).
  authoritativeSupply: isCurrentSasTeachingMember,
  requiresExplicitRelationship: false,
  allowedMemberUserIds: null,         // n/a — relationship not required for the SAS School source
  // §11 SAS School may retire legacy blank-`externalSource` holds it historically owned — an EXPLICIT SAS-only switch.
  allowLegacyUnstampedRecords: true,
  enabled: true,
  blockedReason: '',
});

// ── SAS COLLECTIVE operational schedule (SEPARATE source). Distinct sourceId + distinct hold-id prefix so a Collective
// commitment for the SAME person/date/slot can NEVER collide with a SAS-School hold (§12/§13). Currently BLOCKED: no
// current-season spreadsheet identity/access is configured, so the lane cannot read or write (§3/§55). When the
// operator provides the 2026/27 Collective workbook id + a read-only share to the runtime identity + the exact
// source→member relationship, set `spreadsheetId`/`tab` and flip `enabled` — no core-planner change is required. ─────
export const SAS_COLLECTIVE_SCHEDULE_SOURCE = Object.freeze({
  organizationId: 'sas-niseko',
  seasonId: '2026_27',
  sourceId: 'sas_collective_schedule',
  kind: SOURCE_KIND.OPERATIONAL_SCHEDULE_GRID,
  authorityType: SOURCE_AUTHORITY.OPERATIONAL_ALLOCATION, // default from historical format (§5); NOT commercial (§18)
  holdIdPrefix: 'SASCOL',             // distinct namespace — never 'SASHOLD'
  tab: 'MASTER SCHEDULE',             // verified live tab (Pass 81C/81D); parsed by collectiveSchedule.js
  label: 'SAS Collective schedule',
  // §9 — eligibility is teaching + current, CATEGORY-AGNOSTIC. A member's commercial category (school/collective) does
  // NOT decide Collective operational supply; the explicit source relationship does. So the SAME canonical human can be
  // supply for BOTH sources (§8) without any category rewrite.
  authoritativeSupply: isCurrentTeachingMember,
  // §5/§8 — a Collective hold requires an EXPLICIT, org+season+source-scoped member relationship (below). Until the real
  // 2026/27 source→member mapping is supplied, this stays empty: NO member is confirmed, so an exact-name match alone
  // can never establish an unconfirmed relationship — it fails closed as `source_relationship_unconfirmed` (§8).
  requiresExplicitRelationship: true,
  // Pass 81D — the 15 operator-confirmed exact canonical members who participate in SAS Collective 2026/27 (14 exact
  // labels from Pass 81C + Briege after the §8 operator identity correction). Canonical BUSINESS userIds only; never an
  // Auth uid, never a synthetic id. Mykola Pufka stays OUT (unresolved → blocked_unknown_instructor).
  allowedMemberUserIds: [
    'seed_instructor_sam_moorhouse',    // also current SAS School — one human, multiple relationships (§6)
    'seed_instructor_jonathan_ruel',
    'seed_instructor_luis_reid',
    'seed_instructor_chris_painter',
    'seed_instructor_arthur_demonchaux',
    'seed_instructor_alicia_mermillod',
    'seed_instructor_mathis_bozzolo',
    'seed_instructor_jeff_stein',
    'seed_instructor_madelyne_janes',
    'seed_instructor_joe_trotter',
    'seed_instructor_lauren',           // Lauren Price — School category, ALSO a current Collective relationship (§11)
    'seed_instructor_toni_wrobel',      // Toni Wrobel = Toni Prinz, one canonical human (§7)
    'seed_instructor_tomy_lamuniere',
    'seed_instructor_adrian_candeloro',
    'seed_instructor_briege_mcentee',   // source label "Briege Moorhouse" (§8 operator identity correction)
  ],
  // §7/§8 — explicit source LABEL aliases → canonical userId (exact, operator-confirmed; never fuzzy). The source
  // instructor label never creates identity; these record that an alternate label is the SAME existing human.
  sourceAliases: {
    'Toni Prinz': 'seed_instructor_toni_wrobel',       // §7 timesheet-tab label for the same canonical human
    'Briege Moorhouse': 'seed_instructor_briege_mcentee', // §8 operator-confirmed identity correction
  },
  // §11 — a genuinely separate source retires ONLY its OWN records; it must never claim a blank/unstamped legacy hold.
  allowLegacyUnstampedRecords: false,
  // §13 — explicit placeholder tokens for the Collective format (in addition to the generic "Instructor N").
  placeholderTokens: ['tbc', 'tbd', 'unassigned'],
  // The verified live 2026/27 Collective workbook is `1OBPH_-usQGlp9aK4HNZcif7kUbggsMzUovPTwtqYeEk` (season Dec 2026 –
  // May 2027). §15 — the spreadsheet id + read is owned SERVER-SIDE (functions read path), NOT bundled into client code;
  // it is intentionally left null here so the client never carries it. `enabled` flips true only after the §15 secure
  // runtime-Viewer access gate passes (grant the Functions runtime SA Viewer + remove public link-sharing).
  spreadsheetId: null,
  // Pass 81D — ENABLED after all gates passed: secure runtime Viewer read verified (sas-sheet-sync@ SA; public access
  // removed), 15 operator-confirmed member relationships, 12 season-participation repairs, live preflight 175/0/0,
  // SAS School 17/17/16/17 preserved, commercial sync disabled.
  enabled: true,
  blockedReason: '',
});

const REGISTRY = {
  'sas-niseko': {
    '2026_27': [SAS_SCHOOL_SCHEDULE_SOURCE, SAS_COLLECTIVE_SCHEDULE_SOURCE],
  },
};

// All operational-source descriptors declared for an org+season (enabled or not) — the sync lane iterates these.
export function operationalSourcesFor(organizationId = '', seasonId = '2026_27') {
  return (REGISTRY[organizationId] && REGISTRY[organizationId][seasonId]) || [];
}

// Only the sources that are actually connectable right now (have a spreadsheet id and are enabled).
export function enabledOperationalSourcesFor(organizationId = '', seasonId = '2026_27') {
  return operationalSourcesFor(organizationId, seasonId).filter((s) => s.enabled && s.spreadsheetId);
}

// Pass 82A.1 (blocker 3) — organizationId is REQUIRED and defaults to '' (fail closed), never to 'sas-niseko'. A caller
// that omits the org gets `null` (no descriptor), never SAS's descriptor by accident.
export function operationalSourceById(sourceId = '', organizationId = '', seasonId = '2026_27') {
  if (!organizationId) return null;
  return operationalSourcesFor(organizationId, seasonId).find((s) => s.sourceId === sourceId) || null;
}

// ── Pass 82E — SOURCE → OPERATING AREA mapping (CONFIG, not a hard-code) ──────────────────────────────────────────────
// Operator-confirmed: all current SAS operation (master/commercial + grid + collective) runs from the NISEKO operating
// team this winter, so those sources map to `niseko`. This is CONFIG keyed by org+source — never `if org==='sas-niseko'`
// in generic logic. A future Furano source would declare its own area here (or a single source could carry an explicit
// per-row area discriminator); until then Furano source ingestion stays OFF (no silent Niseko default for a new source).
const SOURCE_OPERATING_AREA = {
  'sas-niseko': {
    sas_master_schedule: 'niseko',
    sas_master_schedule_staff: 'niseko',
    sas_schedule_grid: 'niseko',
    sas_collective_schedule: 'niseko',
  },
};
export function sourceOperatingAreaFor(organizationId = '', sourceId = '') {
  const byOrg = SOURCE_OPERATING_AREA[organizationId];
  return (byOrg && byOrg[sourceId]) || '';
}
// The area of a source-backed booking, from its stamped `externalSource` (empty for a non-source / unconfigured source).
export function bookingSourceOperatingArea(booking = {}) {
  return sourceOperatingAreaFor(booking && booking.organizationId, booking && (booking.externalSource || booking.source));
}
